'use client'

import React, { useEffect, useRef, useState } from 'react';


import { Button } from '@/components/ui/button';
import ProjectDetails from '@/components/distribution-analytics/project-details';
import PackageDetails from '@/components/distribution-analytics/package-details';
import SecondaryPackageDetails from '@/components/distribution-analytics/secondary-package-details';
import { ChooseContent } from '@/components/distribution-analytics/choose-content';
import ResultsSection from '@/components/distribution-analytics/results-section';
import { Form } from '@/components/ui/form';
    import { useStepperContext } from '../components/stepper-provider';
    import Stepper from '../components/stepper';
    import PackageContent from '@/components/distribution-analytics/package-content';
    import PalletDistributionContent from '@/components/distribution-analytics/pallet-distribution-content';
import { Input } from '@/components/ui/input';
import { Label } from 'recharts';
import { File, Loader2 } from 'lucide-react';
import { useCreateRequestItemMutation, useGetRequestItemByIdQuery, useUpdateRequestItemMutation, useGetKnnDataQuery, useGetTempUrlsQuery } from '@/redux/slices/stack-and-ship/stack-and-ship-apiSlice';
import { use } from 'react'
import Loading from '@/components/Loading';
import { URLSearchParams } from 'url';
import { useSelector } from 'react-redux';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ResultsTable } from '../components/ResultsTable';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import DistributionFooter from '../components/distribution-footer';
import SpiderChart from '@/components/distribution-analytics/spider-chart';
import { getMockDistributionRequestItem, isMockDistributionProjectId } from '@/mocks/distributionAnalyticsDemo';
import { isMockModeActive } from '@/services/mockAuth';
import { formatKnnMeasurement, formatKnnPercent, getKnnValue } from '@/lib/knnResponse';
const formSchema = z.object({
    // Project Details
    project_name: z.string().min(1, 'Project name is required'),
    brand_name: z.string().min(1, 'Brand name is required'),
    location_name: z.string().min(1, 'Location is required'),
    customer: z.string().min(1, 'Customer is required'),
    application_name: z.string().min(1, 'Application is required'),
    primary_package_type: z.string().min(1, 'Package type is required'),
    has_tertiary_package: z.string().min(1, 'Please select yes or no'),

    // Package Details
    net_contents: z.string().min(1, 'Net contents must be positive'),
    net_contents_unit: z.string(),
    filled_top_load: z.string().min(1, 'Top load must be positive'),
    filled_top_load_unit: z.string(),
    primary_package_diameter: z.string().min(1, 'Diameter must be positive'),
    primary_package_diameter_unit: z.string(),
    primary_package_height: z.string().min(1, 'Height must be positive'),
    primary_package_height_unit: z.string(),
    bottle_design: z.string().optional(),

    // Secondary Package Details
    secondary_package_type: z.string().min(1, 'Secondary package type is required'),
    secondary_topload_filled: z.string().min(1, 'Topload must be positive'),
    secondary_topload_filled_unit: z.string().optional(),
    secondary_weight: z.string().min(1, 'Weight must be positive'),
    secondary_weight_unit: z.string(),
    secondary_length: z.string().min(1, 'Length must be positive'),
    secondary_length_unit: z.string(),
    secondary_width: z.string().min(1, 'Width must be positive'),
    secondary_width_unit: z.string(),
    secondary_height: z.string().min(1, 'Height must be positive'),
    secondary_height_unit: z.string(),

    // Tertiary Package Details
    t_bct: z.string().min(1, 'Please enter BCT value'),
    t_bct_unit: z.string().min(1, 'Please select a unit'),
    t_material: z.string().min(1, 'Please select a material'),
    t_length: z.string().min(1, 'Please enter a length'),
    t_length_unit: z.string().min(1, 'Please select a unit'),
    t_width: z.string().min(1, 'Please enter a width'),
    t_width_unit: z.string().min(1, 'Please select a unit'),
    t_height: z.string().min(1, 'Please enter a height'),
    t_height_unit: z.string().min(1, 'Please select a unit'),
    t_weight: z.string().min(1, 'Please enter a weight'),
    t_weight_unit: z.string().min(1, 'Please select a unit'),

    // Results
    test_results: z.array(z.object({
        test_type: z.string().min(1, 'Test type is required'),
        result: z.string().min(1, 'Result is required'),
        date: z.string().min(1, 'Date is required')
    })).optional(),
    comments: z.string().optional(),
    attachments: z.array(z.string()).optional(),
    pallet_base_type: z.string().min(1, 'Pallet base type is required'),
});
type FormValues = z.infer<typeof formSchema>;

export default function EditDistributionProject({projectId}:{projectId:string}) {
    const { currentStep, handleNext, handleBack, handleToPackage, setCurrentStep } = useStepperContext();
    const [showResults, setShowResults] = useState(false);
    const [showAnalysis, setShowAnalysis] = useState(false);
    const [searchParams] = useSearchParams();
    const newProject = searchParams.get('newProject');
    const navigate = useNavigate();
    const location = useLocation();
    const [nearestNeighbour,setNearestNeighbour] = useState(null)
    const isMockProject = isMockModeActive() && isMockDistributionProjectId(projectId);
    const hasSetInitialMockStep = useRef(false);
    const hasConsumedNewProjectParam = useRef(false);
    useEffect(() => {
        if (newProject === 'yes' && !hasConsumedNewProjectParam.current) {
            hasConsumedNewProjectParam.current = true;
            handleToPackage();
            navigate(location.pathname, { replace: true });
        } else if (isMockProject && !hasSetInitialMockStep.current) {
            hasSetInitialMockStep.current = true;
            setCurrentStep('choose');
        }
    }, [newProject, isMockProject, handleToPackage, setCurrentStep, navigate, location.pathname])
    const resultsRef = useRef<HTMLDivElement>(null);
    const { data: knnData, isLoading: isFetchingKnnData, error: knnError } = useGetKnnDataQuery({
        data: {
            "SPEC NUMBER": "TEST CASE",
            "Application": "Cold Fill",
            "Brand": "Propel",
            "Category": "Hydration",
            "Bottle Design": "Ripples",
            "Volume(ml)": "1000",
            "VolumeUnits": "ml",
            "Filled Topload": "90",
            "Filled Topload_Units": "lbf",
            "Package Material": "PET",
            "Diameter(in)": "73.2",
            "Diameter_Units": "mm",
            "Height(in)": "220",
            "bottleHeight_Units": "mm",
            "FilledWeight(g)": "1100",
            "FilledWeight_Units": "g",
            "Secondary/Tertiary PackageType": "Tray",
            "secondaryBct": "",
            "secondaryBct_Units": "lbf",
            "SecondaryMaterial": "Shrink Bundle",
            "CaseLength(in)": "13.2",
            "CaseLength_units": "in",
            "CaseWidth(in)": "8.9",
            "CaseWidth_Units": "in",
            "CaseHeight(in)": "10.1",
            "CaseHeight_Units": "in",
            "CaseWeight": "",
            "CaseWeight_Units": "g",
            "Pallet Material": "48x40x5 Wood",
            "Layers/Pallet": "6",
            "Pallet Pattern Stackup": "",
            "Cases Per Layer": "",
            "Max Pallets Stacked": 2,
            "Distance(miles)": "1000",
            "Distance_Unit": "miles",
            "StackingPattern": "Square",
            "Shipping Mode": "Road"
        },
        "k": parseInt('5')
    }, {
        skip: isMockProject,
    });
    const { isPending } = useSelector((state: any) => state.stackAndShip)
    const [createRequestItem, { isLoading }] = useCreateRequestItemMutation();

    const [graphData, setGraphData] = useState<any>(null);
    const [updateRequestItem, { isLoading: isLoadingUpdate }] = useUpdateRequestItemMutation();
    const { data: requestItem, isLoading: isLoadingRequestItem, error: requestItemError, refetch } = useGetRequestItemByIdQuery(projectId, {
        skip: isMockProject,
    });
    const effectiveRequestItem = isMockProject ? getMockDistributionRequestItem(projectId) : requestItem;
    const refetchRequestItem = isMockProject ? () => undefined : refetch;
    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            project_name: '',
            brand_name: '',
            location_name: '',
            customer: '',
            application_name: '',
            primary_package_type: '',
            has_tertiary_package: 'no',
            net_contents: '',
            net_contents_unit: 'ml',
            filled_top_load: '',
            filled_top_load_unit: 'lbf',
            primary_package_diameter: '',
            primary_package_diameter_unit: 'in',
            primary_package_height: '',
            primary_package_height_unit: 'in',
            secondary_package_type: '',
            secondary_topload_filled: '',
            secondary_topload_filled_unit: 'lbf',
            secondary_weight: '',
            secondary_weight_unit: 'g',
            secondary_length: '',
            secondary_length_unit: 'in',
            secondary_width: '',
            secondary_width_unit: 'in',
            secondary_height: '',
            secondary_height_unit: 'in',
            t_bct: '',
            t_bct_unit: 'lbf',
            t_material: '',
            t_length: '',
            t_length_unit: 'mm',
            t_width: '',
            t_width_unit: 'mm',
            t_height: '',
            t_height_unit: 'mm',
            t_weight: '',
            t_weight_unit: 'g',
            test_results: [],
            comments: '',
            attachments: [],
            pallet_base_type: ''
        }
    });
    useEffect(() => {
        if (Array.isArray(knnData?.data)) {
            const formattedData = knnData?.data.map((row: any[], index: number) => {
                const columns = knnData.columns || [];
                const getValue = (columnNames: string[]) => getKnnValue(row, columns, columnNames);
                const getDistanceValue = (columnNames: string[]) => getKnnValue(knnData.data_2?.[index] || [], columns, columnNames);
                const isPreSelected = effectiveRequestItem?.chosenCases?.some(
                    (chosenCase: any) => chosenCase.project_name === row[0]
                );
                const brand = getValue(['Brand']);
                const volume = getValue(['Volume(ml)', 'Volume']);
                const volumeUnit = getValue(['VolumeUnits', 'Volume Units']) || (volume ? 'ml' : '');
                const fileName = getValue(['File Name', 'Filename', 'Product File Name']);
                const category = getValue(['Category']);
                const bottleDesign = getValue(['Bottle Design']);
                const diameter = getValue(['Diameter(in)', 'Diameter', 'Bottle Diameter', 'Bottle Diameter(in)']);
                const height = getValue(['Height(in)', 'Height', 'Bottle Height', 'Bottle Height(in)']);
                const secondaryPackageType = getValue(['Secondary/Tertiary PackageType', 'Secondary PackageType', 'Secondary Package Type', 'Package Type']);
                const caseLength = getValue(['CaseLength(in)', 'Case Length(in)', 'Case Length', 'TertiaryCaseLength(in)']);
                const caseWidth = getValue(['CaseWidth(in)', 'Case Width(in)', 'Case Width', 'TertiaryCaseWidth(in)']);
                const caseHeight = getValue(['CaseHeight(in)', 'Case Height(in)', 'Case Height', 'TertiaryCaseHeight(in)']);
                const shippingMode = getValue(['Shipping Mode', 'Transit']) || '';
                const distanceMiles = getValue(['Distance(miles)', 'Distance', 'Transit Distance']);

                const formattedRow = {
                    optionNo: row[0],
                    product: String(fileName || '').trim() || [brand || row[1] || '', volume ? `${volume}${volumeUnit ? ` ${volumeUnit}` : ''}` : ''].filter(Boolean).join(' ') || category || row[2] || '',
                    primary: {
                        details: `${bottleDesign}, ${category} D:${formatKnnMeasurement(diameter, '')} H:${formatKnnMeasurement(height, '')}`,
                        similarity: formatKnnPercent(getDistanceValue(['primary_distance', 'primary_similarity', 'Primary Similarity %']))
                    },
                    secondary: {
                        details: `${secondaryPackageType} L:${formatKnnMeasurement(caseLength, 'in')} W:${formatKnnMeasurement(caseWidth, 'in')} H:${formatKnnMeasurement(caseHeight, 'in')}`,
                        similarity: formatKnnPercent(getDistanceValue(['secondary_distance', 'secondary_similarity', 'Secondary Similarity %']))
                    },
                    pallet_pattern: getValue(['Pallet Pattern Stackup', 'Pallet Pattern', 'Pallet Pattern Design']),
                    // pallet: {
                    //     pattern: row[getColumnIndex('Unit Load Pattern')] || 'N/A',
                    //     image: ''
                    // },
                    transit: [shippingMode, distanceMiles ? `${distanceMiles} Miles` : ''].filter(Boolean).join(' ') || 'No Data',
                    stacking: getValue(['Stacking Pattern', 'StackingPattern']),
                    selected: isPreSelected
                };

                return formattedRow;
            });

            // Initialize selectedRows with pre-selected rows
            const preSelectedRows = formattedData.filter(row => row.selected);


            // Format data for spider chart
            if (formattedData.length > 0) {
                const graphData = {
                    labels: [
                        'Volume',
                        'Filled Weight',
                        'Bottle Diameter',
                        'Bottle Height',
                        'Cases/Layer',
                        'Layers/Pallet',
                    ],
                    currentProject: [100, 100, 100, 100, 100, 100], // Current project is always 100%
                    selectedOption: [
                        formattedData[0].primary.similarity,
                        formattedData[0].secondary.similarity,
                        90, // Example values - replace with actual data
                        85,
                        95,
                        88,
                    ],
                };
                setGraphData(graphData);
            }
        }
    }, [knnData, effectiveRequestItem]);
    const onSubmit = async () => {
        if (currentStep === 'define') {
            const submitButton = document.querySelector('button[type="submit"]');
            submitButton?.click();
        }
        else if (currentStep === 'package') {
            console.log('package rann')
            const submitButton = document.querySelector('button[type="submit"]');
            submitButton?.click();
        } else if (currentStep === 'choose') {
            console.log('choose rann')
            const submitButton = document.getElementById('save-selected-cases-button');
            submitButton?.click();
        } else if (currentStep === 'distribution' || currentStep === 'pallet') {
            const submitButton = document.getElementById('pallet-distribution-save-button');
            submitButton?.click();
        }
        // else {
        //     handleNext();
        // }
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case 'define':
                return <ProjectDetails requestItem={effectiveRequestItem} refetch={refetchRequestItem} />;
            case 'package':
                return <PackageContent nearestNeighbour={nearestNeighbour} setNearestNeighbour={setNearestNeighbour} requestItem={effectiveRequestItem} refetch={refetchRequestItem} />;
            case 'choose':
                return <ChooseContent requestItem={effectiveRequestItem} refetch={refetchRequestItem} />;
            case 'pallet':
            case 'distribution':
                return <PalletDistributionContent requestItem={effectiveRequestItem} refetch={refetchRequestItem} />;
            default:
                return null;
        }
    };
    if (isLoadingRequestItem && !isMockProject) {
        return <Loading />
    }
    if (requestItemError && !isMockProject) {
        return (
            <div className="max-h-[80vh] overflow-y-scroll bg-white">
                <main className="pt-6 pb-8 px-4 mx-auto">
                    <div className="rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                        Failed to load this Distribution Analytics project. Please refresh or try again later.
                    </div>
                </main>
            </div>
        );
    }
    return (
        <div className="max-h-[80vh] overflow-y-scroll bg-white">
            {
                isLoadingRequestItem && !isMockProject && <Loading />
            }
            <main className="pt-6 pb-8 px-4  mx-auto">
                <div >
                    <div className="space-y-8">
                        {renderStepContent()}

                        <div className="flex justify-between space-x-4">
                            {/* <Button
                                type="button"
                                variant="outline"
                                onClick={handleBack}
                                disabled={currentStep === 'define'}
                            >
                                Previous
                            </Button> */}
                            {/* {!showResults && <div className="flex gap-4 pb-[100px] w-full ">
                                {
                                    (currentStep === 'package' || currentStep === 'choose') &&
                                    <div className='w-full '>
                                        <Button disabled={isPending} onClick={onSubmit} className='bg-blue ml-auto flex items-center gap-2'>
                                            {isPending && <Loader2 className='size-4 animate-spin' />}  Save
                                        </Button>
                                    </div>
                                }
                            </div>} */}
                        </div>
                    </div>
                </div>
                <DistributionFooter
                    requestItem={effectiveRequestItem}
                    
                    layout={false}
                    hasUnsavedChanges={true}
                    isLoading={isPending}
                    noNext={currentStep === 'distribution' || currentStep === 'pallet'?true:false}
                    onSubmit={onSubmit}
                />

            </main>
        </div>
    );
}
